import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './components/PrivateRoute';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import DefaultDashboard from './pages/DefaultDashboard';
import AttendancePage from './pages/AttendancePage';
import ProfilePage from './pages/ProfilePage';
import SalesDashboard from './pages/Sales/SalesDashboard';
import LeadCommentsPage from './pages/Sales/LeadCommentsPage';
import LeadActivitiesPage from './pages/Sales/LeadActivitiesPage';
import HRDashboard from './pages/HR/HRDashboard';
import RequirementDetail from './pages/HR/RequirementDetail';
import RequirementForm from './pages/HR/RequirementForm';
import HRCandidateForm from './pages/HR/HRCandidateForm';
import HRCandidateDetail from './pages/HR/HRCandidateDetail';
import StaffingDashboard from './pages/Staffing/StaffingDashboard';
import StaffingRequirementsPage from './pages/Staffing/StaffingRequirementsPage';
import StaffingCandidatesPage from './pages/Staffing/StaffingCandidatesPage';
import StaffingCommentsPage from './pages/Staffing/StaffingCommentsPage';
import StaffingActivitiesPage from './pages/Staffing/StaffingActivitiesPage';
import ClientDetail from './pages/Staffing/ClientDetail';
import ClientForm from './pages/Staffing/ClientForm';
import StaffingRequirementForm from './pages/Staffing/StaffingRequirementForm';
import StaffingRequirementDetail from './pages/Staffing/StaffingRequirementDetail';
import StaffingCandidateForm from './pages/Staffing/StaffingCandidateForm';
import StaffingCandidateDetail from './pages/Staffing/StaffingCandidateDetail';
import EmployeeManagement from './pages/Admin/EmployeeManagement';
import EmployeeEdit from './pages/Admin/EmployeeEdit';
import EmployeeCreate from './pages/Admin/EmployeeCreate';
import DigestEmailPage from './pages/Admin/DigestEmailPage';
import TeamAttendancePage from './pages/Admin/TeamAttendancePage';
import ProjectsDashboard from './pages/Projects/ProjectsDashboard';
import ProjectDetail from './pages/Projects/ProjectDetail';
import { DEPARTMENTS } from './utils/constants';

function App() {
  const { user } = useSelector((state) => state.auth);

  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/sales';
    
    const userDepts = user.departments || [user.department];
    
    // Redirect to first available department dashboard
    if (userDepts.includes(DEPARTMENTS.SALES)) return '/sales';
    if (userDepts.includes(DEPARTMENTS.HR)) return '/hr';
    if (userDepts.includes(DEPARTMENTS.STAFFING_SALES)) return '/staffing';
    return '/dashboard';
  };

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} replace /> : <LoginPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      
      {/* Default Dashboard for employees without specific department access */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowedRoles={['admin', 'manager', 'employee']}>
            <DefaultDashboard />
          </PrivateRoute>
        }
      />

      {/* Attendance Route */}
      <Route
        path="/attendance"
        element={
          <PrivateRoute allowedRoles={['manager', 'employee']}>
            <AttendancePage />
          </PrivateRoute>
        }
      />

      {/* Profile Route */}
      <Route
        path="/profile"
        element={
          <PrivateRoute allowedRoles={['admin', 'manager', 'employee']}>
            <ProfilePage />
          </PrivateRoute>
        }
      />

      {/* Sales Routes */}
      <Route
        path="/sales"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.SALES]}>
            <SalesDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/sales/comments"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.SALES]}>
            <LeadCommentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/sales/activities"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.SALES]}>
            <LeadActivitiesPage />
          </PrivateRoute>
        }
      />

      {/* HR Routes */}
      <Route
        path="/hr"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <HRDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/hr/requirements/new"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <RequirementForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/hr/requirements/:requirementId/candidates/new"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <HRCandidateForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/hr/requirements/:id"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <RequirementDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/hr/candidates/:id"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <HRCandidateDetail />
          </PrivateRoute>
        }
      />

      {/* Staffing Sales Routes */}
      <Route
        path="/staffing"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/requirements"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingRequirementsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/activities"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingActivitiesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/comments"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingCommentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/candidates"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingCandidatesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/clients/new"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <ClientForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/clients/:clientId/requirements/new"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingRequirementForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/requirements/:requirementId/candidates/new"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingCandidateForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/requirements/:id"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingRequirementDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/candidates/:id"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <StaffingCandidateDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/staffing/clients/:id"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.STAFFING_SALES]}>
            <ClientDetail />
          </PrivateRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/employees"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <EmployeeManagement />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/employees/new"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <EmployeeCreate />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/employees/:id"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <EmployeeEdit />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/digest-email"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <DigestEmailPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/team-attendance"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <TeamAttendancePage />
          </PrivateRoute>
        }
      />

      {/* HR Attendance Route */}
      <Route
        path="/hr/team-attendance"
        element={
          <PrivateRoute allowedDepartments={[DEPARTMENTS.HR]}>
            <TeamAttendancePage />
          </PrivateRoute>
        }
      />

      {/* Team Attendance Route - accessible to all (backend will authorize based on role/reporting manager status) */}
      <Route
        path="/team-attendance"
        element={
          <PrivateRoute>
            <TeamAttendancePage />
          </PrivateRoute>
        }
      />

      {/* Projects Routes - accessible to all authenticated employees */}
      <Route
        path="/projects"
        element={
          <PrivateRoute>
            <ProjectsDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <PrivateRoute>
            <ProjectDetail />
          </PrivateRoute>
        }
      />

      {/* Default Routes */}
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
